import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { PageResult, Tag, TagFilter } from "@vp/models";
import { HttpResponseCache } from "@vp/shared/services/cache";
import { createUrlBuilder, mapToPageResult, withHeaderData } from "@vp/shared/utilities";
import { Operation } from "rfc6902";
import { Observable } from "rxjs";
import { TAG_API_BASE_URL } from "../state+/tags-state";

export type TagModel = Omit<
  Partial<Tag>,
  "lastUpdatedBy" | "lastUpdatedDateTime" | "createdBy" | "createdDateTime"
>;

@Injectable({
  providedIn: "root"
})
export class TagsApiService {
  private apiBaseUrl = inject(TAG_API_BASE_URL);
  private http = inject(HttpClient);
  private httpResponseCache = inject<HttpResponseCache<Tag[]>>(HttpResponseCache);

  getTags = (
    filter: Partial<TagFilter> = {},
    useCache: boolean = true,
    cacheExpireTime: number = 5000
  ): Observable<PageResult<Tag>> => {
    const urlBuilder = createUrlBuilder(this.apiBaseUrl, Object.keys(filter));
    const urlWithQueryString = urlBuilder.build({}, filter);

    if (useCache) {
      return this.httpResponseCache
        .getCachedData(urlWithQueryString, "tags", cacheExpireTime, urlWithQueryString)
        .pipe(withHeaderData(), mapToPageResult<Tag>());
    }

    return this.http
      .get<Tag[]>(urlWithQueryString, { observe: "response" })
      .pipe(withHeaderData(), mapToPageResult<Tag>());
  };

  addTag = (tag: TagModel): Observable<Tag> => {
    return this.http.post<Tag>(`${this.apiBaseUrl}/${tag.tagId}`, tag);
  };

  updateTag = (tag: TagModel): Observable<Tag> => {
    return this.http.put<Tag>(`${this.apiBaseUrl}/${tag.tagId}`, tag);
  };

  deleteTag = (tagId: string): Observable<Tag> => {
    return this.http.delete<Tag>(`${this.apiBaseUrl}/${tagId}`);
  };

  patch = (tagId: string, operations: Operation[]) => {
    const apiURL = `${this.apiBaseUrl}/${tagId}`;
    return this.http.patch(apiURL, operations);
  };
}
